/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
} from "@mui/material";

function Timer() {
  const [workTime, setWorkTime] = useState(25); // Default work time (25 minutes)
  const [breakTime, setBreakTime] = useState(5); // Default short break time (5 minutes)
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(workTime * 60); // Time in seconds
  const [isWorkSession, setIsWorkSession] = useState(true);

  useEffect(() => {
    let timer;

    if (isTimerActive) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else {
      clearInterval(timer);
    }

    return () => clearInterval(timer);
  }, [isTimerActive]);

  useEffect(() => {
    if (timeLeft <= 0) {
      if (isWorkSession) {
        setIsWorkSession(false); // Switch to break
        setTimeLeft(breakTime * 60);
      } else {
        setIsWorkSession(true); // Switch back to work
        setTimeLeft(workTime * 60);
      }
    }
  }, [timeLeft, isWorkSession, breakTime, workTime]);

  const handleStartStop = () => {
    setIsTimerActive((prev) => !prev);
  };

  const handleReset = () => {
    setIsTimerActive(false);
    setTimeLeft(workTime * 60); // Reset to work time
    setIsWorkSession(true);
  };

  const handleFocusTimer = () => {
    setWorkTime(25); // Focus work time
    setBreakTime(5); // Short break time
    setIsWorkSession(true); // Start with work session
    setTimeLeft(25 * 60); // Set to 25 minutes in seconds
  };

  const handleShortBreak = () => {
    setWorkTime(25); // Focus work time
    setBreakTime(5); // Short break time
    setIsWorkSession(false); // Start with break session
    setTimeLeft(5 * 60); // Set to 5 minutes in seconds
  };

  const handleLongBreak = () => {
    setWorkTime(25); // Focus work time
    setBreakTime(30); // Long break time
    setIsWorkSession(false); // Start with break session
    setTimeLeft(30 * 60); // Set to 15 minutes in seconds
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

   // Function to show notifications
   const showNotification = (title, message) => {
    if (Notification.permission === "granted") {
      new Notification(title, {
        body: message,
        icon: "https://cdn-icons-png.flaticon.com/512/3479/3479007.png", // Timer icon
      });
    }
  };
  const notificationSound = new Audio("https://www.soundjay.com/button/beep-07.wav");
  useEffect(() => {
    if (timeLeft <= 0) {
      if (isWorkSession) {
        setIsWorkSession(false);
        setTimeLeft(breakTime * 60);
        showNotification("Break Time", "Time to take a break!");
        notificationSound.play();
      } else {
        setIsWorkSession(true);
        setTimeLeft(workTime * 60);
        showNotification("Focus Time", "Time to get back to work!");
        notificationSound.play();
      }
    }
  }, [timeLeft, isWorkSession, breakTime, workTime]);


  useEffect(() => {
    const requestPermission = () => {
      if (Notification.permission !== "granted") {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") {
            console.log("Notification permission granted.");
            notificationSound.play();
          } else {
            console.log("Notification permission denied.");
          }
        });
      }
    };
  
    requestPermission();
  }, []);
  

  return (
    <Container
    maxWidth="xl"
    sx={{
      p: 2,
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      // backgroundImage: "url('https://images.pexels.com/photos/414171/pexels-photo-414171.jpeg?auto=compress&cs=tinysrgb&w=1600')",
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <Card
      sx={{
        borderRadius: "20px",
        boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.12)",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        p: 4,
        textAlign: "center",
        maxWidth: 420,
        width: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h5" sx={{ fontSize: "2rem", fontWeight: 600, color: "#0D1B2A", mb: 2 }}>
          {isWorkSession ? "Focus Session" : "Break Session"}
        </Typography>
  
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: "#0077b6",
            mb: 3,
            py: 2,
            px: 3,
            borderRadius: "16px",
            backgroundColor: "#e0f2ff",
            fontSize: "3rem",
            letterSpacing: "1.5px",
          }}
        >
          {formatTime(timeLeft)}
        </Typography>
  
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            sx={{
              fontSize: "1.1rem",
              px: 4,
              py: 1.5,
              borderRadius: "50px",
              backgroundColor: isTimerActive ? "#ef476f" : "#06d6a0",
              color: "#fff",
              "&:hover": {
                backgroundColor: isTimerActive ? "#d73c5b" : "#05c194",
              },
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
            onClick={handleStartStop}
          >
            {isTimerActive ? "Pause" : "Start"}
          </Button>
  
          <Button
            variant="outlined"
            sx={{
              fontSize: "1.1rem",
              px: 4,
              py: 1.5,
              borderRadius: "50px",
              borderColor: "#9e9e9e",
              color: "#333",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            onClick={handleReset}
          >
            Reset
          </Button>
        </Box>
  
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            sx={{
              fontSize: "1rem",
              px: 3,
              py: 1.2,
              borderRadius: "40px",
              backgroundColor: "#0077b6",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#005f87",
              },
            }}
            onClick={handleFocusTimer}
          >
            Focus
          </Button>
  
          <Button
            variant="contained"
            sx={{
              fontSize: "1rem",
              px: 3,
              py: 1.2,
              borderRadius: "40px",
              backgroundColor: "#f9a825",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#f57f17",
              },
            }}
            onClick={handleShortBreak}
          >
            Short Break
          </Button>
  
          <Button
            variant="contained"
            sx={{
              fontSize: "1rem",
              px: 3,
              py: 1.2,
              borderRadius: "40px",
              backgroundColor: "#ff7043",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#f4511e",
              },
            }}
            onClick={handleLongBreak}
          >
            Long Break
          </Button>
        </Box>
      </CardContent>
    </Card>
  </Container>
  
  );
}

export default Timer;
